import {
  Outlet,
  createRootRoute,
  useLocation,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import {
  QueryClientProvider,
  useQueryErrorResetBoundary,
} from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";

import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { memo, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { RotateCwIcon } from "lucide-react";
import { persistOptions, queryClient } from "@/lib/query";
import { GlobalProvider } from "@/components/context/GlobalProvider";
import { Devtools } from "@/components/helpers/Devtools";
import { captureEvent } from "@/lib/analytics";
import { scrubUrl } from "@/lib/auth/scrub";

export const Route = createRootRoute({
  component: memo(RootComponent),
  errorComponent: memo(ErrorComponent),
});

function RootComponent() {
  return (
    <GlobalProvider>
      <div className="h-screen w-screen bg-[#0a0a0a] font-sans antialiased">
        <TooltipProvider>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={persistOptions}
          >
            <QueryClientProvider client={queryClient}>
              <RouteObserver />
              <Outlet />
              <Toaster position="bottom-center" richColors />
              <Devtools />
            </QueryClientProvider>
          </PersistQueryClientProvider>
        </TooltipProvider>
      </div>
    </GlobalProvider>
  );
}

function RouteObserver() {
  const location = useLocation();
  const searchKeys = useMemo(() => {
    const params = new URLSearchParams(location.searchStr || "");
    return Array.from(new Set(params.keys()));
  }, [location.searchStr]);

  useEffect(() => {
    captureEvent("platform_route_viewed", {
      href: scrubUrl(location.href),
      pathname: location.pathname,
      hash: location.hash ? "[present]" : "",
      searchKeys,
    });
  }, [location.href, location.pathname, location.hash, searchKeys]);

  return null;
}

function ErrorComponent({ error }: ErrorComponentProps) {
  const router = useRouter();
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <div className="flex flex-col gap-4 rounded-xl border border-red-950 bg-red-900 p-4 shadow-xl">
        {message && <span className="font-bold text-white">{message}</span>}
        <div className="flex max-h-[calc(100dvh-400px)] flex-col gap-2 overflow-y-auto">
          {stack?.split("\n").map((line, i) => (
            <span key={i} className="text-white">
              {line}
            </span>
          ))}
        </div>
        <div className="flex justify-end">
          <Button
            variant="default"
            onClick={() => {
              router.invalidate();
            }}
          >
            <RotateCwIcon />
            Retry
          </Button>
        </div>
      </div>
    </div>
  );
}
